import {
  Mode,
  SearchFilter,
  UserRef,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { MoneySearchFilter } from '../MoneySearchFilter';
import { ReferenceNumber } from '../ReferenceNumber';
import { UomSearchFilter } from '../search/filter/UomSearchFilter';
import { SlimGLCode } from '../SlimGLCode';

import { Order } from './Order';

export class OrderSearchCriteria extends SearchFilter<Order> {
  id?: number;
  orderId: number[] = [];
  excludedIds: number[] = [];
  ownerNaturalIds: string[] = [];
  orderStatus: string[] = [];
  originLocationId?: number;
  originLocationName?: string;
  originLocationCode?: string;
  originCity?: string;
  originState?: string;
  originPostalCode?: string;
  originCountry?: string;
  originLocationGroupIds: string[] = [];
  destinationLocationId?: number;
  destinationLocationName?: string;
  destinationLocationCode?: string;
  destinationCity?: string;
  destinationState?: string;
  destinationPostalCode?: string;
  destinationCountry?: string;
  destinationLocationGroupIds: string[] = [];
  directions: string[] = [];
  equipmentTypes: string[] = [];
  requestedPickupDateRange?: ZonedDateTimeRange;
  pickupAppointmentRequired: boolean | null = null;
  requestedDeliveryDateRange?: ZonedDateTimeRange;
  deliveryAppointmentRequired: boolean | null = null;
  createdDateRange?: ZonedDateTimeRange;
  savedSearchId?: number;
  productNumber?: string;
  referenceNumbers: ReferenceNumber[] = [];
  glCodes: SlimGLCode[] = [];
  tags: string[] = [];
  assignedUsers: UserRef[] = [];
  hazmat: boolean | null = null;
  swc: boolean | null = null;
  customerRouted: boolean | null = null;
  hot: boolean | null = null;
  hasAnyUnplannedShipOrders: boolean | null = null;
  freightAllowanceEnabled: boolean | null = null;
  freightAllowance?: MoneySearchFilter;
  cancellationReason?: string;
  referenceSort?: boolean = false;
  requestedMode: Mode[] = [];
  weight?: UomSearchFilter;
  handlingUnit?: UomSearchFilter;
  source?: string;
  onlyUnplannedShipOrders = false;

  constructor(opts?: Partial<OrderSearchCriteria>) {
    super(opts);

    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.orderId != null) {
      // There are legacy saved links where orderId is not an array that we need to convert here
      this.orderId = Array.isArray(opts.orderId)
        ? opts.orderId
        : [opts.orderId];
    }
    if (opts?.excludedIds != null) {
      this.excludedIds = opts.excludedIds;
    }
    if (opts?.ownerNaturalIds != null) {
      this.ownerNaturalIds = opts.ownerNaturalIds;
    }
    if (opts?.orderStatus != null) {
      this.orderStatus = opts.orderStatus;
    }
    if (opts?.originLocationId != null) {
      this.originLocationId = opts.originLocationId;
    }
    if (opts?.originLocationName != null) {
      this.originLocationName = opts.originLocationName;
    }
    if (opts?.originLocationCode != null) {
      this.originLocationCode = opts.originLocationCode;
    }
    if (opts?.originCity != null) {
      this.originCity = opts.originCity;
    }
    if (opts?.originState != null) {
      this.originState = opts.originState;
    }
    if (opts?.originPostalCode != null) {
      this.originPostalCode = opts.originPostalCode;
    }
    if (opts?.originCountry != null) {
      this.originCountry = opts.originCountry;
    }
    if (opts?.originLocationGroupIds != null) {
      this.originLocationGroupIds = [...opts.originLocationGroupIds];
    }
    if (opts?.destinationLocationId != null) {
      this.destinationLocationId = opts.destinationLocationId;
    }
    if (opts?.destinationLocationName != null) {
      this.destinationLocationName = opts.destinationLocationName;
    }
    if (opts?.destinationLocationCode != null) {
      this.destinationLocationCode = opts.destinationLocationCode;
    }
    if (opts?.destinationCity != null) {
      this.destinationCity = opts.destinationCity;
    }
    if (opts?.destinationState != null) {
      this.destinationState = opts.destinationState;
    }
    if (opts?.destinationPostalCode != null) {
      this.destinationPostalCode = opts.destinationPostalCode;
    }
    if (opts?.destinationCountry != null) {
      this.destinationCountry = opts.destinationCountry;
    }
    if (opts?.destinationLocationGroupIds != null) {
      this.destinationLocationGroupIds = [...opts.destinationLocationGroupIds];
    }
    if (opts?.directions != null) {
      this.directions = opts.directions;
    }
    if (opts?.equipmentTypes != null) {
      this.equipmentTypes = opts.equipmentTypes;
    }
    if (opts?.requestedPickupDateRange != null) {
      this.requestedPickupDateRange = new ZonedDateTimeRange(
        opts.requestedPickupDateRange,
      );
    }
    if (opts?.pickupAppointmentRequired != null) {
      this.pickupAppointmentRequired = opts?.pickupAppointmentRequired;
    }
    if (opts?.requestedDeliveryDateRange != null) {
      this.requestedDeliveryDateRange = new ZonedDateTimeRange(
        opts.requestedDeliveryDateRange,
      );
    }
    if (opts?.deliveryAppointmentRequired != null) {
      this.deliveryAppointmentRequired = opts?.deliveryAppointmentRequired;
    }
    if (opts?.createdDateRange != null) {
      this.createdDateRange = new ZonedDateTimeRange(opts.createdDateRange);
    }
    if (opts?.savedSearchId != null) {
      this.savedSearchId = opts.savedSearchId;
    }
    if (opts?.sortField != null) {
      this.sortField = opts.sortField;
    } else {
      this.sortField = undefined;
    }
    if (opts?.productNumber != null) {
      this.productNumber = opts.productNumber;
    }
    if (opts?.referenceNumbers != null) {
      this.referenceNumbers = opts.referenceNumbers.map(
        (r) => new ReferenceNumber(r),
      );
    }
    if (opts?.glCodes != null) {
      this.glCodes = opts.glCodes.map((g) => new SlimGLCode(g));
    }
    if (opts?.tags != null) {
      this.tags = opts.tags;
    }
    if (opts?.assignedUsers != null) {
      // Full users carry avatar URLs into the search query param, which freezes the page with many selected
      this.assignedUsers = opts.assignedUsers
        .filter(notNull)
        .map(
          ({ id, username, displayName }) =>
            new UserRef({ id, username, displayName }),
        );
    }
    if (opts?.hazmat != null) {
      this.hazmat = opts?.hazmat;
    }
    if (opts?.swc != null) {
      this.swc = opts?.swc;
    }
    if (opts?.customerRouted != null) {
      this.customerRouted = opts?.customerRouted;
    }
    if (opts?.hot != null) {
      this.hot = opts?.hot;
    }
    if (opts?.hasAnyUnplannedShipOrders != null) {
      this.hasAnyUnplannedShipOrders = opts?.hasAnyUnplannedShipOrders;
    }
    if (opts?.freightAllowanceEnabled != null) {
      this.freightAllowanceEnabled = opts?.freightAllowanceEnabled;
    }
    if (opts?.freightAllowance != null) {
      this.freightAllowance = new MoneySearchFilter(opts.freightAllowance);
    }
    if (opts?.cancellationReason != null) {
      this.cancellationReason = opts?.cancellationReason;
    }
    if (opts?.referenceSort != null) {
      this.referenceSort = opts?.referenceSort;
    }
    if (opts?.requestedMode != null) {
      this.requestedMode = opts.requestedMode;
    }
    if (opts?.weight != null) {
      this.weight = new UomSearchFilter(opts.weight);
    }
    if (opts?.handlingUnit != null) {
      this.handlingUnit = new UomSearchFilter(opts.handlingUnit);
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.onlyUnplannedShipOrders != null) {
      this.onlyUnplannedShipOrders = opts.onlyUnplannedShipOrders;
    }

    this.sortField = opts?.sortField ?? 'id';
    this.sortDirection = opts?.sortDirection ?? 'DESC';
  }

  get ids(): number[] {
    return this.orderId;
  }

  set ids(ids: number[]) {
    this.orderId = ids;
  }

  get isOrderSearchCriteria(): true {
    return true;
  }

  static isOrderSearchCriteria(obj: unknown): obj is OrderSearchCriteria {
    return !!obj && (obj as OrderSearchCriteria).isOrderSearchCriteria === true;
  }
}
